import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { loginEmail, loginOtp, loginSocial, selectAuthLoading } from '../../store/slices/authSlice';
import { authAPI } from '../../services/api';
import { Button, Input, Tabs } from '../../components/common/UI';
import logoImg from '../../assets/logo.png';
import GoogleLoginButton from '../../components/auth/GoogleLoginButton';

const TABS = [
  { value: 'email', label: 'Email' },
  { value: 'otp', label: 'Mobile OTP' },
];

const navigateByRole = (user, navigate) => {
  const role = user?.role;
  if (['super_admin', 'admin', 'moderator'].includes(role)) {
    navigate('/admin');
  } else if (['business_owner', 'business_staff'].includes(role)) {
    navigate('/business');
  } else {
    navigate('/');
  }
};

// ── Email form ────────────────────────────────────────
const EmailForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const loading = useSelector(selectAuthLoading);
  const { register, handleSubmit, formState: { errors } } = useForm();
  const [showPassword, setShowPassword] = useState(false);

  const onSubmit = async (data) => {
    try {
      const result = await dispatch(loginEmail(data));
      if (loginEmail.fulfilled.match(result)) {
        navigateByRole(result.payload.user, navigate);
      }
    } catch (err) {
      toast.error(err.message || 'Login failed');
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <Input
        label="Email address"
        type="email"
        placeholder="you@company.com"
        error={errors.email?.message}
        {...register('email', { required: 'Email is required', pattern: { value: /\S+@\S+\.\S+/, message: 'Invalid email' } })}
      />
      <Input
        label="Password"
        type={showPassword ? 'text' : 'password'}
        placeholder="••••••••"
        error={errors.password?.message}
        suffix={
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="focus:outline-none focus:text-brand-600 transition-colors"
          >
            {showPassword ? (
              <svg className="h-5 w-5 text-surface-400 hover:text-surface-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l18 18" />
              </svg>
            ) : (
              <svg className="h-5 w-5 text-surface-400 hover:text-surface-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            )}
          </button>
        }
        {...register('password', { required: 'Password is required', minLength: { value: 6, message: 'Min 6 characters' } })}
      />
      <div className="flex justify-end">
        <Link to="/forgot-password" className="text-xs text-brand-600 hover:underline">Forgot password?</Link>
      </div>
      <Button type="submit" variant="primary" size="lg" className="w-full" loading={loading}>Sign In</Button>
    </form>
  );
};

// ── OTP form ──────────────────────────────────────────
const OtpForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const loading = useSelector(selectAuthLoading);
  const [step, setStep] = useState('phone');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [sending, setSending] = useState(false);

  const sendOtp = async () => {
    if (!phone.match(/^\+?[0-9]{10,13}$/)) return toast.error('Enter a valid phone number');
    setSending(true);
    try {
      await authAPI.sendOtp({ phone, purpose: 'login' });
      toast.success('OTP sent!');
      setStep('otp');
    } catch (e) {
      toast.error(e.response?.data?.message || e.response?.data?.msg || 'Failed to send OTP');
    } finally { setSending(false); }
  };

  const verifyOtp = async () => {
    const result = await dispatch(loginOtp({ phone, code, purpose: 'login' }));
    if (loginOtp.fulfilled.match(result)) {
      navigateByRole(result.payload.user, navigate);
    }
  };

  if (step === 'phone') return (
    <div className="space-y-4">
      <Input label="Phone number" type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="+91 98765 43210" prefix={<svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>} />
      <Button variant="primary" size="lg" className="w-full" onClick={sendOtp} loading={sending}>Send OTP</Button>
    </div>
  );

  return (
    <div className="space-y-4">
      <p className="text-sm text-surface-600">OTP sent to <strong>{phone}</strong></p>
      <div>
        <label className="label">Enter 6-digit OTP</label>
        <input
          type="text" maxLength={6} value={code} onChange={e => setCode(e.target.value.replace(/\D/, ''))}
          placeholder="• • • • • •"
          className="input text-center text-xl tracking-[1rem] font-mono"
        />
      </div>
      <Button variant="primary" size="lg" className="w-full" onClick={verifyOtp} loading={loading} disabled={code.length < 6}>Verify & Sign In</Button>
      <button onClick={() => setStep('phone')} className="w-full text-xs text-surface-500 hover:text-surface-700 underline">← Change number</button>
    </div>
  );
};

// ── Main Login Page ───────────────────────────────────
export default function LoginPage() {
  const [tab, setTab] = useState('email');
  const [googleLoading, setGoogleLoading] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleGoogleLogin = async (e) => {
    e.preventDefault();
    setGoogleLoading(true);
    try {
      const userEmail = prompt("Enter your Google Account email for Google Sign-In:");
      if (!userEmail) {
        setGoogleLoading(false);
        return;
      }
      const socialId = `google_user_${Date.now()}`;
      const result = await dispatch(loginSocial({
        email: userEmail,
        social_id: socialId,
        social_type: 'Google',
        device_type: 'Web'
      }));

      if (loginSocial.fulfilled.match(result)) {
        const user = result.payload?.user || result.payload;
        navigateByRole(user, navigate);
      }
    } catch (err) {
      toast.error('Google Sign-In failed');
    } finally {
      setGoogleLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex gradient-mesh">
      {/* Left panel */}
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-center items-center p-16 relative overflow-hidden">
        <div className="absolute inset-0 gradient-brand opacity-95" />
        <div className="relative z-10 text-white text-center max-w-sm">
          <div className="mb-6 flex items-center justify-center">
            <img src={logoImg} alt="Bellezbuy Admin" className="h-20 w-20 object-contain drop-shadow-xl" />
          </div>
          <h1 className="font-display text-4xl font-bold mb-4">Bellezbuy Admin</h1>
          <p className="text-white/80 text-lg leading-relaxed">Manage your marketplace, monitor sellers, track orders, and grow your platform.</p>
          <div className="mt-10 grid grid-cols-3 gap-4">
            {[['99.9%', 'Uptime'], ['50K+', 'Products'], ['10K+', 'Orders']].map(([val, lbl]) => (
              <div key={lbl} className="rounded-2xl bg-white/10 backdrop-blur-sm p-4">
                <div className="font-display text-2xl font-bold">{val}</div>
                <div className="text-xs text-white/70 mt-0.5">{lbl}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right panel */}
      <div className="flex w-full lg:w-1/2 flex-col justify-center items-center p-8">
        <div className="w-full max-w-[420px]">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <img src={logoImg} alt="Bellezbuy Admin" className="h-9 w-9 object-contain" />
            <span className="font-display font-bold text-surface-900 text-lg">Bellezbuy Admin</span>
          </div>

          <h2 className="font-display text-2xl font-bold text-surface-900 mb-1">Welcome back</h2>
          <p className="text-sm text-surface-500 mb-8">Sign in to your account to continue</p>

          {/* Google OAuth */}
          <button
            type="button"
            onClick={handleGoogleLogin}
            disabled={googleLoading}
            className="flex w-full items-center justify-center gap-3 rounded-xl border border-surface-200 bg-white px-4 py-2.5 text-sm font-medium text-surface-700 shadow-card hover:bg-surface-50 transition-colors mb-6 cursor-pointer"
          >
            <svg className="h-4 w-4" viewBox="0 0 24 24">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
            </svg>
            {googleLoading ? 'Connecting to Google...' : 'Continue with Google'}
          </button>

          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-surface-200" /></div>
            <div className="relative flex justify-center"><span className="bg-surface-50 px-3 text-xs text-surface-400">or continue with</span></div>
          </div>

          {/* Tabs */}
          <div className="mb-6">
            <Tabs tabs={TABS} active={tab} onChange={setTab} />
          </div>

          {tab === 'email' ? <EmailForm /> : <OtpForm />}

          <p className="mt-6 text-center text-xs text-surface-500">
            New business?{' '}
            <Link to="/register" className="text-brand-600 font-medium hover:underline">Create an account</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
